/* eslint-disable react/jsx-no-bind */
import { ErrorBoundary } from "~/lib/Bugsnag";
import { ErrorView } from "~/components/shared/ErrorView";
import { useNavigate } from "react-router-dom";
import { TableView } from "~/components/build/Show/components/TableView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { fetcher } from "~/components/build/Show/lib/queries";
import { useFilterStore } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { noop, unionWith } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { startTransition, useCallback, useEffect, useMemo } from "react";
import { Job } from "~/components/build/Show/lib/types";

type JobsData = {
  records: Job[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
};

/**
 * Hook for returning a function used for optimistically updatng the query cache
 * for job related queries.
 *
 * Updating the cache allows us to avoid rendering skeleton/loading states in cases
 * where we've already loaded the job.
 */
const useUpdateQueryCacheForJobs = () => {
  const queryClient = useQueryClient();
  const { build } = useBuild();

  return useCallback(
    (jobs: Job[]) => {
      const steps = new Map<string, Job[]>();

      jobs.map((job) => {
        // Group all the jobs by step uuid
        const stepJobs = steps.get(job.stepUuid) || [];
        stepJobs.push(job);
        steps.set(job.stepUuid, stepJobs);

        // Pre-populate the jobs/:id query
        queryClient.setQueryData(["build", build.number, "jobs", job.id], { job });
      });

      // Pre-populate the step/:id/jobs query
      for (const [stepUuid, jobs] of steps) {
        queryClient.setQueryData<Job[]>(
          ["build", build.number, "steps", stepUuid, "jobs"],
          (prevData) => {
            // No previous data, return the incoming jobs.
            if (prevData === undefined) {
              return jobs;
            }
            // Existing jobs exists, merge the two collections together.
            return unionWith(prevData, jobs, (value, other) => value.id === other.id);
          },
        );
      }
    },
    [queryClient, build.number],
  );
};

export const Table = ({ className }) => {
  const { build } = useBuild();
  const filters = useFilterStore((state) => state.filterBy);
  const tableSearchValue = useBuildStore((state) => state.tableSearchValue);
  const showRetriedJobs = useBuildPreferencesStore((state) => state.showRetriedJobs);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    dataUpdatedAt,
  } = useInfiniteQuery<JobsData, Error, InfiniteData<JobsData>, unknown[], string | null>({
    queryKey: [
      "build",
      build.number,
      "jobs",
      {
        filters,
        search: tableSearchValue,
        ...(Features.BuildTableHideRetriedJobs && { showRetriedJobs }),
      },
    ],
    queryFn: ({ pageParam }) => {
      const queryParams = new URLSearchParams();
      if (tableSearchValue) {
        queryParams.set("search", tableSearchValue);
      }

      if (pageParam) {
        queryParams.set("after", pageParam);
      }
      if (filters.length > 0) {
        queryParams.set("state", filters.join(","));
      }

      // Include retried jobs when the preference is enabled (only with BuildTableHideRetriedJobs)
      if (Features.BuildTableHideRetriedJobs && showRetriedJobs) {
        queryParams.set("include_retried_jobs", "true");
      }

      return fetcher<JobsData>(`${build.buildDataBasePath}/jobs?${queryParams.toString()}`);
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.endCursor : null),
    placeholderData: keepPreviousData,
    enabled: Features.ProgressiveBuildShow,
    staleTime: 1000 * 30, // 30 seconds
  });

  const jobs = useMemo(() => data?.pages.flatMap((page) => page.records) ?? [], [data]);

  // Pre-populate common job queries with jobs found in table view.
  const updateQueryCacheForJobs = useUpdateQueryCacheForJobs();
  useEffect(() => {
    const lastPageOfJobs = data?.pages[data.pages.length - 1].records || [];

    startTransition(() => {
      updateQueryCacheForJobs(lastPageOfJobs);
    });
  }, [dataUpdatedAt, updateQueryCacheForJobs]);

  return Features.ProgressiveBuildShow ? (
    <TableView
      jobs={jobs}
      filters={filters}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      className={className}
    />
  ) : (
    <TableView
      jobs={build.jobs}
      filters={filters}
      hasNextPage={false}
      isLoading={false}
      isFetching={false}
      isFetchingNextPage={false}
      fetchNextPage={noop}
      className={className}
    />
  );
};

export default function TablePage() {
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      FallbackComponent={() => <ErrorView className="px-4 py-2" clearError={() => navigate(0)} />}
    >
      <div
        className="relative min-w-0 flex-auto rounded-b-md bg-white contain-layout"
        data-testid="table"
      >
        <Table className="absolute" />
      </div>
    </ErrorBoundary>
  );
}
